import React, { useMemo, useContext } from "react"
import truncate from "../../../utils/Truncate"
import { GENDERS, AGE_RANGES } from "./aiTargetingConstants"
import UserContext from "../../../UserContext"
import { openResourcePreviewModal } from "../ResourcePreviewModal"

function buildSummaryMatrix(targetedAds, defaultAd) {
  const defaultAdObj = defaultAd?.ad || null

  return GENDERS.map(({ value: gender, label, icon, color }) => {
    const cells = AGE_RANGES.map(({ value: age }) => {
      const exactMatch = targetedAds.find(
        (ad) => ad.gender === gender && ad.ageRange?.includes(age)
      )
      if (exactMatch) {
        return { type: "targeted", name: exactMatch.ad?.name, ad: exactMatch.ad }
      }

      const genderAllAges = targetedAds.find(
        (ad) => ad.gender === gender && (!ad.ageRange || ad.ageRange.length === 0)
      )
      if (genderAllAges) {
        return { type: "targeted", name: genderAllAges.ad?.name, ad: genderAllAges.ad }
      }

      const allGendersExactAge = targetedAds.find(
        (ad) => ad.gender === null && ad.ageRange?.includes(age)
      )
      if (allGendersExactAge) {
        return { type: "targeted", name: allGendersExactAge.ad?.name, ad: allGendersExactAge.ad }
      }

      const allGendersAllAges = targetedAds.find(
        (ad) => ad.gender === null && (!ad.ageRange || ad.ageRange.length === 0)
      )
      if (allGendersAllAges) {
        return { type: "targeted", name: allGendersAllAges.ad?.name, ad: allGendersAllAges.ad }
      }

      if (defaultAdObj) {
        return { type: "default", name: defaultAdObj.name, ad: defaultAdObj }
      }

      return { type: "empty", name: null, ad: null }
    })

    return { gender, label, icon, color, cells }
  })
}

function AiTargetingSummaryMatrix({ aiTargetingAds }) {
  const userContext = useContext(UserContext)
  const defaultAd = aiTargetingAds.find((ad) => ad.isDefault)
  const targetedAds = aiTargetingAds.filter((ad) => !ad.isDefault)

  const rows = useMemo(
    () => buildSummaryMatrix(targetedAds, defaultAd),
    [targetedAds, defaultAd]
  )

  if (aiTargetingAds.length === 0) return null

  return (
    <>
      <div className="ai-section-header ai-section-header--summary">
        <div className="ai-section-header__icon-badge ai-section-header__icon-badge--blue">
          <i className="fas fa-th"></i>
        </div>
        <div className="ai-section-header__text">
          <span className="ai-section-header__title">Matrice de synthèse</span>
          <span className="ai-section-header__subtitle">Vue d'ensemble du ciblage configuré</span>
        </div>
      </div>

      <div className="ai-summary-matrix">
        <div className="table-responsive">
          <table className="table table-bordered ai-summary-matrix__table">
            <thead>
              <tr>
                <th className="ai-summary-matrix__corner"></th>
                {AGE_RANGES.map(({ value }) => (
                  <th key={value} className="ai-summary-matrix__col-header">{value}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ gender, label, icon, color, cells }) => (
                <tr key={gender}>
                  <th className="ai-summary-matrix__row-header">
                    <span className={`badge badge-${color} ai-summary-matrix__gender-badge`}>
                      <i className={`${icon} mr-1`}></i>
                      {label}
                    </span>
                  </th>
                  {cells.map((cell, i) => (
                    <td
                      key={AGE_RANGES[i].value}
                      className={`ai-summary-matrix__cell ai-summary-matrix__cell--${cell.type}${cell.ad ? " ai-summary-matrix__cell--clickable" : ""}`}
                      title={cell.name ? `Prévisualiser ${cell.name}` : "—"}
                      onClick={cell.ad ? () => openResourcePreviewModal(cell.ad, userContext) : undefined}
                      style={cell.ad ? { cursor: "pointer" } : undefined}
                    >
                      {cell.type === "default" && (
                        <i className="fas fa-star ai-summary-matrix__default-icon"></i>
                      )}
                      {cell.type === "targeted" && (
                        <i className="fas fa-crosshairs ai-summary-matrix__targeted-icon"></i>
                      )}
                      <span className="ai-summary-matrix__resource-name">
                        {cell.name ? truncate(cell.name, 14) : "—"}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </>
  )
}

export default React.memo(AiTargetingSummaryMatrix)
